import { Checkbox, FlexBox, Text } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import type { Tag, TagState } from 'lib-shared-types-api';
import { useCallback, useMemo, useState } from 'react';

import { tagsApi } from 'apis/tags';
import {
  CategorizedList,
  CategorizedListProps,
} from 'components/categorizedList';
import { useCategorizedList } from 'utils/useCategorizedList';

import { TagCreateItemContent } from './components/tagCreateItemContent/tagCreateItemContent';
import { TagItemContent } from './components/tagItemContent/tagItemContent';

export type CategorizedTagsListProps = Pick<
  CategorizedListProps<Tag>,
  'onChange'
> & {
  /** The selected tags. */
  selectedTags: readonly Tag[];
  /** Whether the list is active. */
  isActive: boolean;
};

/** Represents the categorized tags list. */
export const CategorizedTagsList = ({
  selectedTags,
  isActive,
  onChange,
}: CategorizedTagsListProps): JSX.Element => {
  const hasManageTagsAccess = useHasAccess('settings.tag');
  const [tagState, setTagState] = useState<TagState | undefined>('active');
  const tagCategoriesResult = tagsApi.useReadTagCategoriesQuery();

  const {
    categories,
    favorites,
    hasNextPage,
    asyncStatus,
    arg,
    changeArg,
    search,
    nextPage,
    refetch,
  } = useCategorizedList({
    identityFn: (tag) => tag.id,
    apis: {
      categorized: {
        result: tagCategoriesResult,
        searchOptions: { keys: ['name'], threshold: 0.4 },
        changeArgItemPredicate: (tag, arg) =>
          typeof arg.state === 'undefined' ||
          (arg.state === 'active' && !tag.is_deactivated) ||
          (arg.state === 'inactive' && tag.is_deactivated),
      },
      paginated: {
        endpoint: tagsApi.endpoints.readTags,
        initialArg: { state: tagState },
        itemsSelector: (response) => response.data.tags,
      },
    },
  });

  const handleTagStateChange = useCallback(() => {
    const state: TagState | undefined = !tagState ? 'active' : undefined;

    setTagState(state);
    changeArg({ state, limit: undefined, offset: undefined });

    ampli.conversationHeaderTagIncludeDeactivatedToggled({
      include: !state,
    });
  }, [changeArg, tagState]);

  const showCreate = useMemo(() => {
    const searchTerm = arg?.searchTerm;

    return (
      hasManageTagsAccess &&
      asyncStatus === 'fulfilled' &&
      typeof searchTerm !== 'undefined' &&
      searchTerm.trim().length > 1 &&
      categories.every((category) =>
        category.items.every(
          (tag) =>
            tag.name.toLocaleLowerCase() !== searchTerm.trim().toLowerCase(),
        ),
      )
    );
  }, [arg?.searchTerm, hasManageTagsAccess, asyncStatus, categories]);

  return (
    <CategorizedList
      categories={categories}
      selectedItems={selectedTags}
      asyncStatus={asyncStatus}
      hasNextPage={hasNextPage}
      showSearch
      isMultiple
      resetSearchOnInactive
      isActive={isActive}
      isItemDisabled={(tag) =>
        tag.is_deactivated && !selectedTags.some(({ id }) => id === tag.id)
      }
      isItemFavorite={(tag) => favorites.has(tag.id)}
      identityFn={(tag) => tag.id}
      categoriesTopSlot={
        <FlexBox alignItems="center" gap="200" padding="200 300" as="label">
          <Checkbox
            name="active"
            checked={!tagState}
            onChange={handleTagStateChange}
          />
          <Text.Paragraph variant="3">Include deactivated tags</Text.Paragraph>
        </FlexBox>
      }
      ItemContent={TagItemContent}
      CreateItemContent={showCreate ? TagCreateItemContent : undefined}
      nextPage={nextPage}
      refetch={refetch}
      search={search}
      onChange={onChange}
    />
  );
};
